/**
 * PlanCraft Page - Mobile Strategy Engine
 *
 * AI-guided Business Model Canvas interview interface.
 * Uses the bmc_agent backend to conduct structured conversations.
 *
 * Features:
 * - Chat interface with AI agent
 * - Progress indicator for BMC completion
 * - Export to PDF functionality
 */

import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Send,
  Download,
  Brain,
  Target,
  Users,
  DollarSign,
  Truck,
  Key,
  Handshake,
  Activity,
  CreditCard,
  Loader2,
  CheckCircle,
  Sparkles,
} from 'lucide-react';
import { UserAvatarMenu } from './UserAvatarMenu';

const API_URL = import.meta.env.VITE_API_URL || 'https://claude-essay-agent-production.up.railway.app';

// BMC Fields with icons and descriptions
const BMC_FIELDS = [
  { id: 'value_proposition', name: 'Value Proposition', icon: Target, color: 'indigo' },
  { id: 'customer_segments', name: 'Customer Segments', icon: Users, color: 'blue' },
  { id: 'channels', name: 'Channels', icon: Truck, color: 'cyan' },
  { id: 'customer_relationships', name: 'Customer Relationships', icon: Handshake, color: 'teal' },
  { id: 'revenue_streams', name: 'Revenue Streams', icon: DollarSign, color: 'green' },
  { id: 'key_resources', name: 'Key Resources', icon: Key, color: 'amber' },
  { id: 'key_activities', name: 'Key Activities', icon: Activity, color: 'orange' },
  { id: 'key_partnerships', name: 'Key Partnerships', icon: Handshake, color: 'rose' },
  { id: 'cost_structure', name: 'Cost Structure', icon: CreditCard, color: 'purple' },
];

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface PlanCraftPageProps {
  onBack: () => void;
  getAuthHeaders?: () => Promise<Record<string, string>>;
  onSettingsClick?: () => void;
  onMFAClick?: () => void;
}

export const PlanCraftPage: React.FC<PlanCraftPageProps> = ({
  onBack,
  getAuthHeaders,
  onSettingsClick,
  onMFAClick,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [currentField, setCurrentField] = useState<string>('value_proposition');
  const [completedFields, setCompletedFields] = useState<string[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [profileId, setProfileId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const completionPercentage = Math.round((completedFields.length / BMC_FIELDS.length) * 100);

  // Initialize session on mount
  useEffect(() => {
    initializeSession();
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const initializeSession = async () => {
    setIsLoading(true);
    try {
      const headers = getAuthHeaders ? await getAuthHeaders() : {};

      // Step 1: Get or create a business profile
      let bpId = profileId;
      if (!bpId) {
        const profilesRes = await fetch(`${API_URL}/api/v1/plancraft/profiles`, {
          headers: { ...headers },
        });
        if (profilesRes.ok) {
          const profiles = await profilesRes.json();
          if (profiles.length > 0) {
            bpId = profiles[0].id;
          } else {
            // Auto-create a default profile
            const createRes = await fetch(`${API_URL}/api/v1/plancraft/profiles`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...headers },
              body: JSON.stringify({ business_name: 'My Business', is_primary: true }),
            });
            if (createRes.ok) {
              const newProfile = await createRes.json();
              bpId = newProfile.id;
            }
          }
        }
        if (bpId) setProfileId(bpId);
      }

      if (!bpId) throw new Error('Could not create business profile');

      // Step 2: Start the interview
      const response = await fetch(`${API_URL}/api/v1/plancraft/interview/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({
          business_profile_id: bpId,
          persona_id: 'friendly_coach',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setSessionId(data.session_id);

        // Add the initial greeting
        if (data.message) {
          setMessages([{
            id: Date.now().toString(),
            role: 'assistant',
            content: data.message,
            timestamp: new Date(),
          }]);
        }

        if (data.current_field) {
          setCurrentField(data.current_field);
        }
        if (data.fields_completed) {
          setCompletedFields(data.fields_completed);
        }
      } else {
        // Fallback greeting if API fails
        setMessages([{
          id: Date.now().toString(),
          role: 'assistant',
          content: "Hi! I'm your Strategy Coach. Let's build your Business Model Canvas together. Start by telling me about your business - what problem are you solving and for whom?",
          timestamp: new Date(),
        }]);
      }
    } catch (error) {
      console.error('Failed to initialize session:', error);
      // Fallback greeting
      setMessages([{
        id: Date.now().toString(),
        role: 'assistant',
        content: "Hi! I'm your Strategy Coach. Let's build your Business Model Canvas together. Start by telling me about your business - what problem are you solving and for whom?",
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading || !sessionId) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const headers = getAuthHeaders ? await getAuthHeaders() : {};
      const response = await fetch(`${API_URL}/api/v1/plancraft/interview/continue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({
          session_id: sessionId,
          message: userMessage.content,
        }),
      });

      if (response.ok) {
        const data = await response.json();

        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: data.message,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, assistantMessage]);

        if (data.current_field) {
          setCurrentField(data.current_field);
        }
        if (data.fields_completed) {
          setCompletedFields(data.fields_completed);
        }
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: "I apologize, but I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleExportPDF = async () => {
    if (!sessionId || isExporting) return;

    setIsExporting(true);
    try {
      const headers = getAuthHeaders ? await getAuthHeaders() : {};
      const exportId = profileId || sessionId;
      const response = await fetch(`${API_URL}/api/v1/plancraft/profiles/${exportId}/export?format=pdf`, {
        headers: { ...headers },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'business-model-canvas.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Failed to export PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getCurrentFieldInfo = () => {
    return BMC_FIELDS.find((f) => f.id === currentField) || BMC_FIELDS[0];
  };

  const currentFieldInfo = getCurrentFieldInfo();
  const CurrentFieldIcon = currentFieldInfo.icon;

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between safe-area-inset-top">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-emerald-100 rounded-lg">
              <Brain className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h1 className="font-bold text-gray-900">PlanCraft</h1>
              <p className="text-xs text-gray-500">Strategy Engine</p>
            </div>
          </div>
        </div>

        {/* Export Button & User Menu */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportPDF}
            disabled={isExporting || completedFields.length === 0}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              completedFields.length > 0
                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isExporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Export
          </button>
          <UserAvatarMenu
            onSettingsClick={onSettingsClick}
            onMFAClick={onMFAClick}
          />
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="bg-white px-4 py-3 border-b border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <CurrentFieldIcon className={`w-4 h-4 text-${currentFieldInfo.color}-600`} />
            <span className="text-sm font-medium text-gray-700">
              {currentFieldInfo.name}
            </span>
          </div>
          <span className="text-sm text-gray-500">
            {completionPercentage}% complete
          </span>
        </div>

        {/* Progress bar */}
        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-emerald-500 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${completionPercentage}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>

        {/* Field indicators */}
        <div className="flex gap-1 mt-2">
          {BMC_FIELDS.map((field) => (
            <div
              key={field.id}
              className={`flex-1 h-1 rounded-full ${
                completedFields.includes(field.id)
                  ? 'bg-emerald-500'
                  : field.id === currentField
                  ? 'bg-amber-400'
                  : 'bg-gray-200'
              }`}
              title={field.name}
            />
          ))}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        <AnimatePresence>
          {messages.map((message) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-emerald-600 text-white rounded-br-md'
                    : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm'
                }`}
              >
                {message.role === 'assistant' && (
                  <div className="flex items-center gap-1.5 mb-1">
                    <Sparkles className="w-3 h-3 text-emerald-500" />
                    <span className="text-xs font-medium text-emerald-600">Coach</span>
                  </div>
                )}
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Loading indicator */}
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-start"
          >
            <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <motion.div
                    className="w-2 h-2 bg-emerald-400 rounded-full"
                    animate={{ y: [0, -5, 0] }}
                    transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                  />
                  <motion.div
                    className="w-2 h-2 bg-emerald-400 rounded-full"
                    animate={{ y: [0, -5, 0] }}
                    transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                  />
                  <motion.div
                    className="w-2 h-2 bg-emerald-400 rounded-full"
                    animate={{ y: [0, -5, 0] }}
                    transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                  />
                </div>
                <span className="text-xs text-gray-400">Thinking...</span>
              </div>
            </div>
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Completion Banner */}
      {completionPercentage === 100 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-4 mb-2 bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700">
              Business Model Canvas Complete!
            </span>
          </div>
          <button
            onClick={handleExportPDF}
            className="text-sm font-medium text-emerald-600 hover:text-emerald-700"
          >
            Export PDF
          </button>
        </motion.div>
      )}

      {/* Input Area */}
      <div className="bg-white border-t border-gray-200 px-4 py-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Share your thoughts..."
            className="flex-1 px-4 py-3 bg-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
            disabled={isLoading || !sessionId}
          />
          <button
            onClick={sendMessage}
            disabled={!inputValue.trim() || isLoading || !sessionId}
            className={`p-3 rounded-xl transition-all ${
              inputValue.trim() && !isLoading && sessionId
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlanCraftPage;
